import { Text } from '@noorden-insurtech/alba-design-system';
import type { PmeCoveragesServicesView } from './map-policy-to-pme-view.util';

const COVERAGE_GRID = 'grid grid-cols-[2fr_1.2fr_1fr_1fr] gap-4';
const SERVICE_GRID = 'grid grid-cols-[3.2fr_1fr_1fr] gap-4';
const DEMONSTRATIVE_GRID = 'grid grid-cols-[3fr_1fr_1fr_1fr] gap-4';

function HeaderCell({
  children,
  align = 'left',
}: {
  children: string;
  align?: 'left' | 'right';
}) {
  return (
    <Text
      className={`text-xs font-bold text-alba-black-500 ${
        align === 'right' ? 'text-right' : ''
      }`}
    >
      {children}
    </Text>
  );
}

function BodyCell({
  children,
  align = 'left',
  strong = false,
}: {
  children: string;
  align?: 'left' | 'right';
  strong?: boolean;
}) {
  return (
    <Text
      className={`text-sm ${align === 'right' ? 'text-right' : ''} ${
        strong ? 'font-bold text-alba-black-500' : 'text-alba-gray-600'
      }`}
    >
      {children}
    </Text>
  );
}

function EmptyRow({ label }: { label: string }) {
  return <Text className='py-3 text-sm text-alba-gray-600'>{label}</Text>;
}

type Props = {
  data: PmeCoveragesServicesView;
};

/** As três tabelas da seção "Coberturas e Serviços" do mockup. */
export function PmePolicyCoveragesServices({ data }: Props) {
  return (
    <div className='flex flex-col gap-8'>
      {/* Coberturas */}
      <div className='flex flex-col gap-4'>
        <Text className='text-base text-alba-black-500'>Coberturas</Text>
        <div className='overflow-x-auto'>
          <div className='min-w-[640px] rounded-xl border border-alba-gray-300'>
            <div
              className={`${COVERAGE_GRID} border-b border-alba-gray-300 px-4 py-3`}
            >
              <HeaderCell>Coberturas</HeaderCell>
              <HeaderCell align='right'>Capital Segurado (IS) Total</HeaderCell>
              <HeaderCell align='right'>Taxa Média</HeaderCell>
              <HeaderCell align='right'>Prêmio Mensal</HeaderCell>
            </div>

            {data.coverages.length === 0 ? (
              <div className='px-4'>
                <EmptyRow label='Nenhuma cobertura no demonstrativo.' />
              </div>
            ) : (
              data.coverages.map(coverage => (
                <div
                  key={coverage.name}
                  className={`${COVERAGE_GRID} px-4 py-2`}
                >
                  <div className='flex flex-col'>
                    <BodyCell>{coverage.name}</BodyCell>
                    {coverage.detail ? (
                      <Text className='text-xs text-alba-gray-600'>
                        {coverage.detail}
                      </Text>
                    ) : null}
                  </div>
                  <BodyCell align='right'>
                    {coverage.totalInsuredCapital}
                  </BodyCell>
                  <BodyCell align='right'>{coverage.averageRate}</BodyCell>
                  <BodyCell align='right'>{coverage.monthlyPremium}</BodyCell>
                </div>
              ))
            )}

            <div
              className={`${COVERAGE_GRID} border-t border-alba-gray-300 px-4 py-3`}
            >
              <BodyCell strong>Taxa Média Mensal</BodyCell>
              <span />
              <span />
              <BodyCell align='right' strong>
                {data.averageMonthlyRate}
              </BodyCell>
            </div>
          </div>
        </div>
      </div>

      {/* Serviços e Benefícios */}
      <div className='flex flex-col gap-4'>
        <Text className='text-base text-alba-black-500'>
          Serviços e Benefícios
        </Text>
        <div className='overflow-x-auto'>
          <div className='min-w-[640px] rounded-xl border border-alba-gray-300'>
            <div
              className={`${SERVICE_GRID} border-b border-alba-gray-300 px-4 py-3`}
            >
              <HeaderCell>Serviços / Benefícios</HeaderCell>
              <HeaderCell align='right'>Valor Por Segurado</HeaderCell>
              <HeaderCell align='right'>Valor Mensal</HeaderCell>
            </div>

            {data.services.length === 0 ? (
              <div className='px-4'>
                <EmptyRow label='Nenhum serviço contratado.' />
              </div>
            ) : (
              data.services.map(service => (
                <div key={service.name} className={`${SERVICE_GRID} px-4 py-2`}>
                  <BodyCell>{service.name}</BodyCell>
                  <BodyCell align='right'>{service.valuePerInsured}</BodyCell>
                  <BodyCell align='right'>{service.monthlyValue}</BodyCell>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Demonstrativo — valores anuais, direto do cálculo */}
      <div className='flex flex-col gap-4'>
        <Text className='text-base text-alba-black-500'>Demonstrativo</Text>
        <div className='overflow-x-auto'>
          <div className='min-w-[640px] rounded-xl border border-alba-gray-300'>
            <div
              className={`${DEMONSTRATIVE_GRID} border-b border-alba-gray-300 px-4 py-3`}
            >
              <HeaderCell>Descrição</HeaderCell>
              <HeaderCell align='right'>Valor</HeaderCell>
              <HeaderCell align='right'>IOF</HeaderCell>
              <HeaderCell align='right'>Total</HeaderCell>
            </div>

            {data.demonstrative.length === 0 ? (
              <div className='px-4'>
                <EmptyRow label='Demonstrativo indisponível.' />
              </div>
            ) : (
              data.demonstrative.map(row => (
                <div
                  key={row.description}
                  className={`${DEMONSTRATIVE_GRID} px-4 py-2`}
                >
                  <BodyCell>{row.description}</BodyCell>
                  <BodyCell align='right'>{row.value}</BodyCell>
                  <BodyCell align='right'>{row.iof}</BodyCell>
                  <BodyCell align='right'>{row.total}</BodyCell>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
